import React from 'react';
import {
  YearsOfExperience,
  TechDomainExperience,
  AWSServiceExperience,
  APGLUserProfileRecordSubType,
} from '@amzn/awscat-pattern-library-service-model';
import { reorderByKey } from '../../common/Arrays';
import constants from '../../common/constants';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import BrowserHistory from '../utils/BrowserHistory';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import RouterPaths from '../../common/RouterPaths';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';

export interface ExpertiseItem {
  domainExpertise: string;
  yearsOfExperience: YearsOfExperience;
  id: number;
  rank: number;
}

/** Stable sortable key for an expertise item. */
export const keyOfExpertise = (item: ExpertiseItem): string => String(item.id);

/** Returns the items with ranks renumbered from zero in their current order. */
export const updateItemRanks = (
  itemsWithOutdatedRanks: readonly ExpertiseItem[],
): ExpertiseItem[] =>
  itemsWithOutdatedRanks.map(({ domainExpertise, yearsOfExperience, id }, index) => ({
    domainExpertise,
    yearsOfExperience,
    id,
    rank: index,
  }));

/** Reorders expertise items to match the given keys, then renumbers their ranks. */
export const reorderExpertiseItems = (
  items: readonly ExpertiseItem[],
  orderedKeys: readonly string[],
): ExpertiseItem[] => updateItemRanks(reorderByKey(items, orderedKeys, keyOfExpertise));

export enum ExpertiseType {
  AWSService = 'AWSService',
  TechDomain = 'TechDomain',
}

export interface PublicationItem {
  contentId: string;
  id: number;
}

/** Stable sortable key for a publication item. */
export const keyOfPublication = (item: PublicationItem): string => String(item.id);

/** Reorders the visible prefix by the given keys and appends the hidden tail unchanged. */
export const reorderVisiblePublications = (
  items: readonly PublicationItem[],
  visibleCount: number,
  orderedKeys: readonly string[],
): PublicationItem[] => [
  ...reorderByKey(items.slice(0, visibleCount), orderedKeys, keyOfPublication),
  ...items.slice(visibleCount),
];

export interface OtherPublicationItem {
  title: string;
  url: string;
  id: number;
}

export enum PublicationType {
  AuthoredContent = 'AuthoredContent',
  ReviewedContent = 'ReviewedContent',
}

export enum FormErrorType {
  DescriptionError = 'DescriptionError',
  DomainDuplicationError = 'DomainDuplicationError',
  ServiceDuplicationError = 'ServiceDuplicationError',
  DomainInputError = 'DomainInputError',
  ServiceInputError = 'ServiceInputError',
  PubDuplicationError = 'PubDuplicationError',
  ReviewDuplicationError = 'ReviewDuplicationError',
  OtherPubDuplicationError = 'OtherPubDupliationError',
  OtherPubInputError = 'OtherPubInputError',
}

export const FormErrorTextMap = new Map<FormErrorType, string>([
  [FormErrorType.DomainDuplicationError, 'Duplicated technical domain selection.'],
  [FormErrorType.ServiceDuplicationError, 'Duplicated AWS service selection.'],
  [FormErrorType.DomainInputError, 'Invalid technical domain or years of experience selection.'],
  [FormErrorType.ServiceInputError, 'Invalid AWS service or years of experience selection.'],
  [FormErrorType.PubDuplicationError, 'Duplicated authored content selection.'],
  [FormErrorType.ReviewDuplicationError, 'Duplicated reviewed content selection.'],
  [
    FormErrorType.OtherPubDuplicationError,
    'Duplicated titles or URLs for publications on other platforms.',
  ],
  [FormErrorType.OtherPubInputError, 'Invalid title or URL for publications on other platforms.'],
]);

export const ExpertiseNameMap = new Map<ExpertiseType, string>([
  [ExpertiseType.AWSService, 'AWS service name'],
  [ExpertiseType.TechDomain, 'Domain name'],
]);

export const ExpertiseHeaderNameMap = new Map<ExpertiseType, string>([
  [ExpertiseType.AWSService, 'AWS services'],
  [ExpertiseType.TechDomain, 'Domain'],
]);

export const ExpertiseTextMap = new Map<ExpertiseType, string>([
  [ExpertiseType.AWSService, 'AWS services'],
  [ExpertiseType.TechDomain, 'technical domains'],
]);

export const ExpertiseShortTextMap = new Map<ExpertiseType, string>([
  [ExpertiseType.AWSService, 'service'],
  [ExpertiseType.TechDomain, 'domain'],
]);

export const AddExpertiseTextMap = new Map<ExpertiseType, string>([
  [ExpertiseType.AWSService, 'Add new service'],
  [ExpertiseType.TechDomain, 'Add new domain'],
]);

export const updateCountMap = (
  keyToDecrease: string,
  keyToIncrease: string,
  map: Map<string, number>,
) => {
  // @ts-expect-error TS2532 - strict migration
  const decreasedCount = map.get(keyToDecrease) - 1;
  // @ts-expect-error TS2532 - strict migration
  const increasedCount = map.has(keyToIncrease) ? map.get(keyToIncrease) + 1 : 1;
  if (decreasedCount <= 0) {
    map.delete(keyToDecrease);
  } else {
    map.set(keyToDecrease, decreasedCount);
  }
  map.set(keyToIncrease, increasedCount);
};

// decrease count by 1
export const decreaseCountInCountMap = (key: string, map: Map<string, number>) => {
  // @ts-expect-error TS2532 - strict migration
  const updatedCount = map.get(key) - 1;
  if (updatedCount <= 0) {
    map.delete(key);
  } else {
    map.set(key, updatedCount);
  }
};

export const renderCancelCreateModalButton = (variant: 'normal' | 'link', cancel: () => void) => {
  return (
    <ModalDialogButtonV3
      variant={variant}
      buttonText={constants.CANCEL_BUTTON_TEXT}
      cancelText={constants.KEEP_EDITING_TEXT}
      header={constants.CANCEL_CREATE_TEXT}
      text={constants.CANCEL_CREATE_INSTRUCTION}
      submitButtonText={constants.CANCEL_CREATE_CONFIRMATION}
      onSubmit={cancel}
    />
  );
};

export const renderConfirmCreateModalButton = (confirm: () => void, isCreatingProfile: boolean) => {
  return (
    <ModalDialogButtonV3
      variant={'primary'}
      buttonText={constants.CREATE_PROFILE_BUTTON_TEXT}
      cancelText={constants.KEEP_EDITING_TEXT}
      header={constants.CONFIRM_CREATE_TEXT}
      text={constants.CONFIRM_CREATE_INSTRUCTION}
      submitButtonText={constants.CONFIRM_CREATE_CONFIRMATION}
      onSubmit={confirm}
      loading={isCreatingProfile}
    />
  );
};

export const renderCancelUpdateModalButton = (variant: 'normal' | 'link', cancel: () => void) => {
  return (
    <ModalDialogButtonV3
      variant={variant}
      buttonText={constants.CANCEL_BUTTON_TEXT}
      cancelText={constants.KEEP_EDITING_TEXT}
      header={constants.CANCEL_UPDATE_PROFILE_TEXT}
      text={constants.CANCEL_UPDATE_PROFILE_INSTRUCTION}
      submitButtonText={constants.CANCEL_UPDATE_PROFILE_CONFIRMATION}
      onSubmit={cancel}
    />
  );
};

export const renderUpdateProfileModalButton = (
  confirm: () => void,
  isUpdatingProfile: boolean,
  isUpdateEnabled: boolean,
) => {
  return (
    <ModalDialogButtonV3
      variant={'primary'}
      buttonText={constants.UPDATE_PROFILE_BUTTON_TEXT}
      cancelText={constants.KEEP_EDITING_TEXT}
      header={constants.UPDATE_APGL_USER_PROFILE_TEXT}
      text={constants.UPDATE_APGL_USER_PROFILE_INSTRUCTION}
      submitButtonText={constants.CONFIRM_UPDATE_CONFIRMATION}
      onSubmit={confirm}
      loading={isUpdatingProfile}
      disabled={!isUpdateEnabled}
    />
  );
};

type UserExperience = TechDomainExperience | AWSServiceExperience;

export const displayJoinExperience = (
  experiences: UserExperience[],
  displayFn: (experience: UserExperience) => string,
): string => {
  const maxDisplayNum = 3;
  if (experiences.length <= maxDisplayNum) {
    return experiences.map((experience) => displayFn(experience)).join(', ');
  } else {
    return `${experiences
      .slice(0, maxDisplayNum)
      .map((experience) => displayFn(experience))
      .join(', ')} & ${experiences.length - maxDisplayNum} more`;
  }
};

export const redirectToUserProfilePage = async (userId: string) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const { apglUserProfile } = await mplServiceClient.getAPGLUserProfile(
    userId,
    APGLUserProfileRecordSubType.BasicInfo,
  );
  const isRegistered = !!apglUserProfile.userId;
  if (!isRegistered) {
    BrowserHistory.push(`${RouterPaths.APGL_USER_PROFILE_REGISTRATION_FORM}`);
  } else {
    BrowserHistory.push(`${RouterPaths.APGL_USER_PROFILE_EDITING_FORM}`);
  }
};
